import { RefObject, useLayoutEffect, useRef, useState } from 'react';

// How tall a spacer row below the body must be for the table to fill its
// scroller. AG pins a grid's totals row to the bottom of the grid however
// few rows sit above it; a table is only as tall as its rows, so without the
// spacer DataTable's totals would sit straight under the last row. Once the
// rows overflow the scroller the spacer is 0, and the sticky tfoot keeps the
// totals in view while scrolling, as AG's pinned row does.
const useBodyFillHeight = ({
  isEnabled,
  scrollerRef,
  tableRef,
}: {
  isEnabled: boolean;
  scrollerRef: RefObject<HTMLElement>;
  tableRef: RefObject<HTMLElement>;
}) => {
  const [fillHeight, setFillHeight] = useState<number>(0);
  // The spacer is part of the table's height, so it's taken off again to
  // find the rows' own height
  const fillHeightRef = useRef<number>(0);
  fillHeightRef.current = fillHeight;

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const table = tableRef.current;
    if (!isEnabled || !scroller || !table) {
      setFillHeight(0);
      return;
    }

    const update = () => {
      const contentHeight = table.offsetHeight - fillHeightRef.current;
      const next = Math.max(
        0,
        Math.floor(scroller.clientHeight - contentHeight),
      );
      setFillHeight((current) => (current === next ? current : next));
    };
    update();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    // The card resizing, or the rows changing, changes what's left to fill
    const observer = new ResizeObserver(update);
    observer.observe(scroller);
    observer.observe(table);
    return () => observer.disconnect();
  }, [isEnabled, scrollerRef, tableRef]);

  return fillHeight;
};

export default useBodyFillHeight;
